import { DEFAULT_BALANCING_V1_CONFIG } from "./balancingV1";
import { DEFAULT_CAPACITY_PAYMENTS_CONFIG } from "./capacityPayments";
import { DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG } from "./guaranteesOfOrigin";

export const HYBRID_PARTIAL_ALLOCATION_BASIS_OPTIONS = [
  { value: "capacity_share", label: "Capacity share" },
  { value: "revenue_share", label: "Revenue share" },
];

export const HYBRID_PARTIAL_FEE_INPUT_MODES = [
  { value: "eur_per_mw_year", label: "EUR/MW/year" },
  { value: "eur_per_mw_month", label: "EUR/MW/month" },
  { value: "eur_per_kw_month", label: "EUR/kW/month" },
  { value: "absolute_per_year", label: "Absolute EUR/year" },
  { value: "absolute_per_settlement_period", label: "Absolute EUR/settlement period" },
];

export const HYBRID_PARTIAL_SETTLEMENT_FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Semiannual" },
  { value: "annual", label: "Annual" },
];

export const HYBRID_PARTIAL_PENALTY_MODES = [
  { value: "none", label: "None" },
  { value: "fixed_annual_eur", label: "Fixed annual penalty" },
  { value: "percent_of_gross_fee", label: "% of gross annual fee" },
  { value: "fixed_per_settlement_period", label: "Fixed penalty per settlement period" },
];

export const HYBRID_PARTIAL_RECIPIENT_OPTIONS = [
  { value: "trader_offtaker", label: "Trader/offtaker" },
  { value: "project_owner", label: "Project owner" },
  { value: "shared", label: "Shared" },
  { value: "not_specified", label: "Not specified" },
];

export const HYBRID_PARTIAL_POST_CONTRACT_REVENUE_MODE_OPTIONS = [
  { value: "full_merchant_reversion", label: "Full project reverts to merchant revenue" },
  { value: "zero", label: "No post-contract revenue assumed" },
  { value: "continue_hybrid_structure", label: "Continue hybrid structure after modelled contract term" },
];

export const DEFAULT_HYBRID_PARTIAL_TOLLING = {
  allocationBasis: "capacity_share",
  contractedSharePct: 70,
  merchantSharePct: 30,
  feeInputMode: "eur_per_mw_year",
  feeAmount: null,
  settlementFrequency: "monthly",
  contractTermYears: 10,
  annualEscalationPct: 0,
  capacityBasis: "installed_power_mw",
  contractedCapacityMw: null,
  applyAvailabilityAdjustment: true,
  availabilityAdjustmentPct: 95,
  penaltyMode: "none",
  annualPenaltyEur: null,
  penaltyPctOfGrossFee: null,
  penaltyPerSettlementPeriodEur: null,
  merchantOptimization: true,
  merchantValueBasis: "net_after_charging_cost",
  contractedShareMerchantRecipient: "trader_offtaker",
  merchantShareRevenueRecipient: "project_owner",
  postContractRevenueMode: "full_merchant_reversion",
  includedRevenueStreams: {
    dam: true,
    intraday: false,
    ida1: false,
    ida2: false,
    ida3: false,
    balancing: false,
    ancillary: false,
    capacity: false,
    go: false,
    other: false,
  },
  balancingV1: DEFAULT_BALANCING_V1_CONFIG,
  capacityPayments: DEFAULT_CAPACITY_PAYMENTS_CONFIG,
  guaranteesOfOrigin: DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG,
  bankability: {
    counterpartyStatus: "generic_assumption",
    counterpartyType: "not_specified",
    counterpartyName: "",
    creditSupportType: "none",
    creditSupportMonths: null,
  },
  performanceRegime: {
    guaranteedAvailabilityPct: null,
    penaltyCapPctOfAnnualFee: null,
    plannedOutageAllowanceHours: null,
    guaranteedRtePct: null,
    maxAnnualCycles: null,
    maxAnnualThroughputMWh: null,
    degradationResponsibility: "not_specified",
    augmentationResponsibility: "not_specified",
    chargingCostResponsibility: "mixed",
    dispatchRights: "shared_or_trader",
  },
  hybridPartialPreview: null,
};

const VALID_ALLOCATION_BASIS = new Set(
  HYBRID_PARTIAL_ALLOCATION_BASIS_OPTIONS.map((option) => option.value)
);
const VALID_FEE_INPUT_MODES = new Set(HYBRID_PARTIAL_FEE_INPUT_MODES.map((mode) => mode.value));
const VALID_PENALTY_MODES = new Set(HYBRID_PARTIAL_PENALTY_MODES.map((mode) => mode.value));

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseOptionalFiniteNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function clampPercent(value, fallback = 0) {
  const parsed = parseFiniteNumber(value, fallback);
  return Math.min(100, Math.max(0, parsed));
}

function hasOwnValue(source, key) {
  return Object.prototype.hasOwnProperty.call(source || {}, key);
}

function normalizeSettlementFrequency(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "quarterly" || normalized === "quarter") return "quarterly";
  if (normalized === "semiannual" || normalized === "semi_annually" || normalized === "semi-annually") {
    return "semiannual";
  }
  if (normalized === "annually" || normalized === "annual" || normalized === "yearly") return "annual";
  return "monthly";
}

export function getHybridPartialSettlementPeriodsPerYear(settlementFrequency) {
  switch (normalizeSettlementFrequency(settlementFrequency)) {
    case "annual":
      return 1;
    case "semiannual":
      return 2;
    case "quarterly":
      return 4;
    case "monthly":
    default:
      return 12;
  }
}

function normalizeCapacityBasis(value) {
  const normalized = String(value || "").trim();
  if (normalized === "ContractedMW" || normalized === "contracted_capacity_mw") {
    return "contracted_capacity_mw";
  }
  return "installed_power_mw";
}

function toLegacyCapacityBasis(value) {
  return value === "contracted_capacity_mw" ? "ContractedMW" : "InstalledMW";
}

export function deriveHybridInstalledBessPowerMw(config = {}) {
  const selected = Array.isArray(config?.bess?.selected) ? config.bess.selected : [];
  const totalKw = selected.reduce((sum, battery) => {
    const count = parseFiniteNumber(battery?.count, 1);
    const powerKw = parseFiniteNumber(
      battery?.p_max_d ?? battery?.pMaxD ?? battery?.power ?? battery?.Power,
      0
    );
    return sum + Math.max(0, count) * Math.max(0, powerKw);
  }, 0);
  return totalKw > 0 ? totalKw / 1000 : null;
}

function readYearOneMerchantRevenue(config = {}) {
  const projection = config?.merchantRevenueProjection || {};
  const customRevenues = Array.isArray(projection.customAnnualRevenues)
    ? projection.customAnnualRevenues
    : [];
  const firstCustomRevenue = customRevenues.length ? customRevenues[0] : null;
  const explicit =
    config.year1MerchantRevenueEur ??
    config.yearOneMerchantRevenueEur ??
    config.merchantRevenueYear1 ??
    config.merchantRevenueEurYear1 ??
    config.simulatedYear1MerchantRevenueEur ??
    projection.year1MerchantRevenueEur ??
    projection.yearOneMerchantRevenueEur ??
    projection.merchantRevenueYear1 ??
    (firstCustomRevenue && typeof firstCustomRevenue === "object"
      ? firstCustomRevenue.merchantRevenueEurYear ??
        firstCustomRevenue.merchantRevenueEur ??
        firstCustomRevenue.revenueEurYear ??
        firstCustomRevenue.revenueEur ??
        firstCustomRevenue.value
      : firstCustomRevenue);
  return parseOptionalFiniteNumber(explicit);
}

export function normalizeHybridPartialTolling(config = {}) {
  const hasCanonical =
    config?.hybridPartialTolling && typeof config.hybridPartialTolling === "object";
  const source = hasCanonical ? config.hybridPartialTolling : {};
  const sourceBankability =
    source.bankability && typeof source.bankability === "object" ? source.bankability : {};
  const sourcePerformance =
    source.performanceRegime && typeof source.performanceRegime === "object"
      ? source.performanceRegime
      : {};
  const creditSupportType =
    sourceBankability.creditSupportType ??
    sourceBankability.credit_support_type ??
    DEFAULT_HYBRID_PARTIAL_TOLLING.bankability.creditSupportType;
  const legacyContractedShare = config.hybridContractedSharePct ?? config.tollingContractedSharePct;
  const contractedSharePct = clampPercent(
    source.contractedSharePct ?? source.contracted_share_pct ?? legacyContractedShare,
    DEFAULT_HYBRID_PARTIAL_TOLLING.contractedSharePct
  );
  const merchantSharePct = 100 - contractedSharePct;
  const legacyPenaltyAnnual = parseOptionalFiniteNumber(config.tollingPenaltyAnnual);
  const penaltyMode =
    VALID_PENALTY_MODES.has(source.penaltyMode)
      ? source.penaltyMode
      : legacyPenaltyAnnual && legacyPenaltyAnnual > 0
        ? "fixed_annual_eur"
        : DEFAULT_HYBRID_PARTIAL_TOLLING.penaltyMode;

  return {
    ...DEFAULT_HYBRID_PARTIAL_TOLLING,
    ...source,
    allocationBasis: VALID_ALLOCATION_BASIS.has(source.allocationBasis)
      ? source.allocationBasis
      : DEFAULT_HYBRID_PARTIAL_TOLLING.allocationBasis,
    contractedSharePct,
    merchantSharePct,
    feeInputMode: VALID_FEE_INPUT_MODES.has(source.feeInputMode)
      ? source.feeInputMode
      : DEFAULT_HYBRID_PARTIAL_TOLLING.feeInputMode,
    feeAmount: hasCanonical && hasOwnValue(source, "feeAmount")
      ? parseOptionalFiniteNumber(source.feeAmount)
      : parseOptionalFiniteNumber(
          config.contractedTollFee ?? config.tollingFee ?? config.hybridTollFeePerMWYear
        ),
    settlementFrequency: normalizeSettlementFrequency(
      source.settlementFrequency ?? config.settlementFrequency ?? config.tollingSettlementFrequency
    ),
    contractTermYears: parseFiniteNumber(
      source.contractTermYears ?? config.contractTermYears ?? config.tollingContractYears,
      DEFAULT_HYBRID_PARTIAL_TOLLING.contractTermYears
    ),
    annualEscalationPct: parseFiniteNumber(
      source.annualEscalationPct ?? config.annualEscalationPct ?? config.tollingEscalationPct,
      DEFAULT_HYBRID_PARTIAL_TOLLING.annualEscalationPct
    ),
    capacityBasis: normalizeCapacityBasis(source.capacityBasis ?? config.capacityBasis ?? config.tollingCapacityBasis),
    contractedCapacityMw: parseOptionalFiniteNumber(
      source.contractedCapacityMw ?? source.contracted_capacity_mw
    ),
    applyAvailabilityAdjustment:
      source.applyAvailabilityAdjustment ??
      source.apply_availability_adjustment ??
      DEFAULT_HYBRID_PARTIAL_TOLLING.applyAvailabilityAdjustment,
    availabilityAdjustmentPct: parseOptionalFiniteNumber(
      source.availabilityAdjustmentPct ??
        source.availability_adjustment_pct ??
        config.tollingAvailabilityPct
    ),
    penaltyMode,
    annualPenaltyEur:
      penaltyMode === "fixed_annual_eur"
        ? parseOptionalFiniteNumber(source.annualPenaltyEur ?? config.tollingPenaltyAnnual)
        : parseOptionalFiniteNumber(source.annualPenaltyEur),
    penaltyPctOfGrossFee: parseOptionalFiniteNumber(source.penaltyPctOfGrossFee),
    penaltyPerSettlementPeriodEur: parseOptionalFiniteNumber(source.penaltyPerSettlementPeriodEur),
    merchantOptimization:
      source.merchantOptimization ??
      source.merchant_optimization ??
      config.useMerchantOptimization ??
      DEFAULT_HYBRID_PARTIAL_TOLLING.merchantOptimization,
    merchantValueBasis:
      source.merchantValueBasis ??
      source.merchant_value_basis ??
      DEFAULT_HYBRID_PARTIAL_TOLLING.merchantValueBasis,
    contractedShareMerchantRecipient:
      source.contractedShareMerchantRecipient ??
      source.contracted_share_merchant_recipient ??
      DEFAULT_HYBRID_PARTIAL_TOLLING.contractedShareMerchantRecipient,
    merchantShareRevenueRecipient:
      source.merchantShareRevenueRecipient ??
      source.merchant_share_revenue_recipient ??
      DEFAULT_HYBRID_PARTIAL_TOLLING.merchantShareRevenueRecipient,
    postContractRevenueMode:
      source.postContractRevenueMode ??
      source.post_contract_revenue_mode ??
      DEFAULT_HYBRID_PARTIAL_TOLLING.postContractRevenueMode,
    includedRevenueStreams: {
      ...DEFAULT_HYBRID_PARTIAL_TOLLING.includedRevenueStreams,
      ...(source.includedRevenueStreams || source.included_revenue_streams || {}),
    },
    bankability: {
      ...DEFAULT_HYBRID_PARTIAL_TOLLING.bankability,
      ...sourceBankability,
      counterpartyStatus:
        sourceBankability.counterpartyStatus ??
        sourceBankability.counterparty_status ??
        DEFAULT_HYBRID_PARTIAL_TOLLING.bankability.counterpartyStatus,
      counterpartyType:
        sourceBankability.counterpartyType ??
        sourceBankability.counterparty_type ??
        DEFAULT_HYBRID_PARTIAL_TOLLING.bankability.counterpartyType,
      counterpartyName:
        sourceBankability.counterpartyName ??
        sourceBankability.counterparty_name ??
        DEFAULT_HYBRID_PARTIAL_TOLLING.bankability.counterpartyName,
      creditSupportType,
      creditSupportMonths:
        creditSupportType === "none"
          ? null
          : parseOptionalFiniteNumber(
              sourceBankability.creditSupportMonths ?? sourceBankability.credit_support_months
            ),
    },
    performanceRegime: {
      ...DEFAULT_HYBRID_PARTIAL_TOLLING.performanceRegime,
      ...sourcePerformance,
      guaranteedAvailabilityPct: parseOptionalFiniteNumber(
        sourcePerformance.guaranteedAvailabilityPct ?? sourcePerformance.guaranteed_availability_pct
      ),
      penaltyCapPctOfAnnualFee: parseOptionalFiniteNumber(
        sourcePerformance.penaltyCapPctOfAnnualFee ??
          sourcePerformance.penalty_cap_pct_of_annual_fee
      ),
      plannedOutageAllowanceHours: parseOptionalFiniteNumber(
        sourcePerformance.plannedOutageAllowanceHours ??
          sourcePerformance.planned_outage_allowance_hours
      ),
      guaranteedRtePct: parseOptionalFiniteNumber(
        sourcePerformance.guaranteedRtePct ?? sourcePerformance.guaranteed_rte_pct
      ),
      maxAnnualCycles: parseOptionalFiniteNumber(
        sourcePerformance.maxAnnualCycles ?? sourcePerformance.max_annual_cycles
      ),
      maxAnnualThroughputMWh: parseOptionalFiniteNumber(
        sourcePerformance.maxAnnualThroughputMWh ??
          sourcePerformance.max_annual_throughput_mwh
      ),
      degradationResponsibility:
        sourcePerformance.degradationResponsibility ??
        sourcePerformance.degradation_responsibility ??
        DEFAULT_HYBRID_PARTIAL_TOLLING.performanceRegime.degradationResponsibility,
      augmentationResponsibility:
        sourcePerformance.augmentationResponsibility ??
        sourcePerformance.augmentation_responsibility ??
        DEFAULT_HYBRID_PARTIAL_TOLLING.performanceRegime.augmentationResponsibility,
      chargingCostResponsibility:
        sourcePerformance.chargingCostResponsibility ??
        sourcePerformance.charging_cost_responsibility ??
        DEFAULT_HYBRID_PARTIAL_TOLLING.performanceRegime.chargingCostResponsibility,
      dispatchRights:
        sourcePerformance.dispatchRights ??
        sourcePerformance.dispatch_rights ??
        DEFAULT_HYBRID_PARTIAL_TOLLING.performanceRegime.dispatchRights,
    },
    hybridPartialPreview: null,
  };
}

export function calculateHybridPartialPreview(hybridPartialTolling = {}, config = {}) {
  const normalized = normalizeHybridPartialTolling({ ...config, hybridPartialTolling });
  const installedCapacityMw = deriveHybridInstalledBessPowerMw(config);
  const installedPowerMw = Math.max(0, parseFiniteNumber(installedCapacityMw, 0));
  const contractedShare = clampPercent(normalized.contractedSharePct, 0) / 100;
  const periodsPerYear = getHybridPartialSettlementPeriodsPerYear(normalized.settlementFrequency);
  const contractedCapacityMw =
    normalized.capacityBasis === "contracted_capacity_mw"
      ? Math.max(0, parseFiniteNumber(normalized.contractedCapacityMw, 0))
      : installedPowerMw * contractedShare;
  const merchantCapacityMw = Math.max(0, installedPowerMw - contractedCapacityMw);
  const merchantShare = installedPowerMw > 0 ? merchantCapacityMw / installedPowerMw : 1 - contractedShare;
  const feeAmount = Math.max(0, parseFiniteNumber(normalized.feeAmount, 0));

  let annualGrossContractedFee = 0;
  if (normalized.feeInputMode === "eur_per_mw_year") {
    annualGrossContractedFee = feeAmount * contractedCapacityMw;
  } else if (normalized.feeInputMode === "eur_per_mw_month") {
    annualGrossContractedFee = feeAmount * contractedCapacityMw * 12;
  } else if (normalized.feeInputMode === "eur_per_kw_month") {
    annualGrossContractedFee = feeAmount * contractedCapacityMw * 1000 * 12;
  } else if (normalized.feeInputMode === "absolute_per_year") {
    annualGrossContractedFee = feeAmount;
  } else if (normalized.feeInputMode === "absolute_per_settlement_period") {
    annualGrossContractedFee = feeAmount * periodsPerYear;
  }

  const availabilityFactor =
    normalized.applyAvailabilityAdjustment && normalized.availabilityAdjustmentPct != null
      ? clampPercent(normalized.availabilityAdjustmentPct, 100) / 100
      : 1;

  let annualPenalty = 0;
  if (normalized.penaltyMode === "fixed_annual_eur") {
    annualPenalty = Math.max(0, parseFiniteNumber(normalized.annualPenaltyEur, 0));
  } else if (normalized.penaltyMode === "percent_of_gross_fee") {
    annualPenalty =
      annualGrossContractedFee *
      (Math.max(0, parseFiniteNumber(normalized.penaltyPctOfGrossFee, 0)) / 100);
  } else if (normalized.penaltyMode === "fixed_per_settlement_period") {
    annualPenalty =
      Math.max(0, parseFiniteNumber(normalized.penaltyPerSettlementPeriodEur, 0)) *
      periodsPerYear;
  }

  const annualNetContractedFee = Math.max(
    0,
    annualGrossContractedFee * availabilityFactor - annualPenalty
  );
  const yearOneMerchantRevenue = readYearOneMerchantRevenue(config);
  const projectMerchantRevenue =
    yearOneMerchantRevenue == null ? null : yearOneMerchantRevenue * merchantShare;
  const traderMerchantValue =
    yearOneMerchantRevenue == null ? null : yearOneMerchantRevenue * (1 - merchantShare);
  const estimatedProjectRevenue =
    projectMerchantRevenue == null ? null : annualNetContractedFee + projectMerchantRevenue;
  const estimatedTraderNet =
    traderMerchantValue == null ? null : traderMerchantValue - annualNetContractedFee;

  return {
    installedPowerMw,
    contractedSharePct: contractedShare * 100,
    merchantSharePct: merchantShare * 100,
    contractedCapacityMw,
    merchantCapacityMw,
    annualGrossContractedFee,
    annualPenalty,
    annualNetContractedFee,
    grossContractedFeePerSettlementPeriod: annualGrossContractedFee / periodsPerYear,
    netContractedFeePerSettlementPeriod: annualNetContractedFee / periodsPerYear,
    projectMerchantRevenue,
    traderMerchantValue,
    estimatedProjectRevenue,
    estimatedTraderNet,
    settlementFrequency: normalized.settlementFrequency,
  };
}

export function prepareHybridPartialTollingForPersistence(config = {}) {
  const normalized = normalizeHybridPartialTolling(config);
  const penaltyMode = normalized.penaltyMode;
  const bankability = normalized.bankability || DEFAULT_HYBRID_PARTIAL_TOLLING.bankability;
  const performanceRegime =
    normalized.performanceRegime || DEFAULT_HYBRID_PARTIAL_TOLLING.performanceRegime;

  return {
    ...normalized,
    contractedSharePct: clampPercent(normalized.contractedSharePct, 0),
    merchantSharePct: 100 - clampPercent(normalized.contractedSharePct, 0),
    feeAmount: parseOptionalFiniteNumber(normalized.feeAmount),
    applyAvailabilityAdjustment: Boolean(normalized.applyAvailabilityAdjustment),
    availabilityAdjustmentPct:
      normalized.availabilityAdjustmentPct == null
        ? null
        : clampPercent(normalized.availabilityAdjustmentPct, 100),
    contractedCapacityMw: parseOptionalFiniteNumber(normalized.contractedCapacityMw),
    annualPenaltyEur:
      penaltyMode === "fixed_annual_eur"
        ? parseOptionalFiniteNumber(normalized.annualPenaltyEur)
        : null,
    penaltyPctOfGrossFee:
      penaltyMode === "percent_of_gross_fee"
        ? parseOptionalFiniteNumber(normalized.penaltyPctOfGrossFee)
        : null,
    penaltyPerSettlementPeriodEur:
      penaltyMode === "fixed_per_settlement_period"
        ? parseOptionalFiniteNumber(normalized.penaltyPerSettlementPeriodEur)
        : null,
    bankability: {
      ...DEFAULT_HYBRID_PARTIAL_TOLLING.bankability,
      ...bankability,
      counterpartyName: String(bankability.counterpartyName ?? ""),
      creditSupportMonths:
        bankability.creditSupportType === "none"
          ? null
          : parseOptionalFiniteNumber(bankability.creditSupportMonths),
    },
    performanceRegime: {
      ...DEFAULT_HYBRID_PARTIAL_TOLLING.performanceRegime,
      ...performanceRegime,
      guaranteedAvailabilityPct:
        performanceRegime.guaranteedAvailabilityPct == null
          ? null
          : clampPercent(performanceRegime.guaranteedAvailabilityPct, 0),
      penaltyCapPctOfAnnualFee:
        performanceRegime.penaltyCapPctOfAnnualFee == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.penaltyCapPctOfAnnualFee, 0)),
      plannedOutageAllowanceHours:
        performanceRegime.plannedOutageAllowanceHours == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.plannedOutageAllowanceHours, 0)),
      guaranteedRtePct:
        performanceRegime.guaranteedRtePct == null
          ? null
          : clampPercent(performanceRegime.guaranteedRtePct, 0),
      maxAnnualCycles:
        performanceRegime.maxAnnualCycles == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.maxAnnualCycles, 0)),
      maxAnnualThroughputMWh:
        performanceRegime.maxAnnualThroughputMWh == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.maxAnnualThroughputMWh, 0)),
    },
    hybridPartialPreview: null,
  };
}

export function normalizeProjectHybridPartialTolling(config = {}) {
  const hybridPartialTolling = normalizeHybridPartialTolling(config);
  return {
    ...config,
    hybridPartialTolling,
    hybridContractedSharePct: hybridPartialTolling.contractedSharePct,
    hybridMerchantSharePct: hybridPartialTolling.merchantSharePct,
    hybridTollFeePerMWYear: hybridPartialTolling.feeAmount,
    tollingContractedSharePct: hybridPartialTolling.contractedSharePct,
    tollingMerchantSharePct: hybridPartialTolling.merchantSharePct,
    contractedTollFee: hybridPartialTolling.feeAmount,
    tollingFee: hybridPartialTolling.feeAmount,
    tollingContractYears: hybridPartialTolling.contractTermYears,
    tollingAvailabilityPct: hybridPartialTolling.availabilityAdjustmentPct,
    tollingPenaltyAnnual: hybridPartialTolling.annualPenaltyEur ?? 0,
    tollingSettlementFrequency: hybridPartialTolling.settlementFrequency,
    tollingEscalationPct: hybridPartialTolling.annualEscalationPct,
    tollingCapacityBasis: toLegacyCapacityBasis(hybridPartialTolling.capacityBasis),
    useMerchantOptimization: Boolean(hybridPartialTolling.merchantOptimization),
  };
}

export function prepareProjectHybridPartialTollingForPersistence(config = {}) {
  const hybridPartialTolling = prepareHybridPartialTollingForPersistence(config);
  return {
    ...config,
    hybridPartialTolling,
    hybridContractedSharePct: hybridPartialTolling.contractedSharePct,
    hybridMerchantSharePct: hybridPartialTolling.merchantSharePct,
    hybridTollFeePerMWYear: hybridPartialTolling.feeAmount,
    tollingContractedSharePct: hybridPartialTolling.contractedSharePct,
    tollingMerchantSharePct: hybridPartialTolling.merchantSharePct,
    contractedTollFee: hybridPartialTolling.feeAmount,
    tollingFee: hybridPartialTolling.feeAmount,
    tollingContractYears: hybridPartialTolling.contractTermYears,
    tollingAvailabilityPct: hybridPartialTolling.availabilityAdjustmentPct,
    tollingPenaltyAnnual: hybridPartialTolling.annualPenaltyEur ?? 0,
    tollingSettlementFrequency: hybridPartialTolling.settlementFrequency,
    tollingEscalationPct: hybridPartialTolling.annualEscalationPct,
    tollingCapacityBasis: toLegacyCapacityBasis(hybridPartialTolling.capacityBasis),
    useMerchantOptimization: Boolean(hybridPartialTolling.merchantOptimization),
  };
}
